// Local Notification Service - No backend required
import localAuthService from './localAuthService';

class LocalNotificationService {
  constructor() {
    this.notifications = this.loadNotifications();
    this.settings = this.loadSettings();
    this.templates = this.loadTemplates();
  }

  // Load notifications from localStorage
  loadNotifications() {
    const notifications = localStorage.getItem('silva_notifications');
    return notifications ? JSON.parse(notifications) : [
      {
        id: '1',
        userId: '2',
        type: 'shipment_update',
        title: 'Envío en tránsito',
        message: 'Tu envío SILVA123456789 está en camino',
        priority: 'medium',
        read: false,
        data: {
          trackingNumber: 'SILVA123456789',
          status: 'in_transit'
        },
        createdAt: new Date(Date.now() - 3600000).toISOString()
      },
      {
        id: '2',
        userId: '2',
        type: 'payment_success',
        title: 'Pago exitoso',
        message: 'Tu pago de $150.00 ha sido procesado',
        priority: 'high',
        read: false,
        data: {
          amount: 150.00,
          transactionId: 'TXN123456'
        },
        createdAt: new Date(Date.now() - 7200000).toISOString()
      },
      {
        id: '3',
        userId: '2',
        type: 'delivery_complete',
        title: 'Envío entregado',
        message: 'Tu envío SILVA987654321 ha sido entregado',
        priority: 'medium',
        read: true,
        data: {
          trackingNumber: 'SILVA987654321',
          deliveredAt: new Date(Date.now() - 86400000).toISOString()
        },
        createdAt: new Date(Date.now() - 86400000).toISOString()
      }
    ];
  }

  // Save notifications to localStorage
  saveNotifications() {
    localStorage.setItem('silva_notifications', JSON.stringify(this.notifications));
  }

  // Load settings from localStorage
  loadSettings() {
    const settings = localStorage.getItem('silva_notification_settings');
    return settings ? JSON.parse(settings) : {
      '2': {
        email: {
          enabled: true,
          types: ['shipment_update', 'payment_success', 'delivery_complete', 'security_alert']
        },
        push: {
          enabled: true,
          types: ['shipment_update', 'payment_success', 'delivery_complete']
        },
        sms: {
          enabled: false,
          types: ['delivery_complete']
        },
        frequency: 'immediate',
        quietHours: {
          enabled: false,
          start: '22:00',
          end: '08:00'
        }
      }
    };
  }

  // Save settings to localStorage
  saveSettings() {
    localStorage.setItem('silva_notification_settings', JSON.stringify(this.settings));
  }

  // Load templates from localStorage
  loadTemplates() {
    const templates = localStorage.getItem('silva_notification_templates');
    return templates ? JSON.parse(templates) : [
      {
        id: 'shipment_update',
        name: 'Actualización de Envío',
        title: 'Envío {trackingNumber} - {status}',
        message: 'Tu envío {trackingNumber} ha cambiado a estado {status}',
        variables: ['trackingNumber', 'status']
      },
      {
        id: 'payment_success',
        name: 'Pago Exitoso',
        title: 'Pago procesado - ${amount}',
        message: 'Tu pago de ${amount} ha sido procesado exitosamente',
        variables: ['amount']
      },
      {
        id: 'delivery_complete',
        name: 'Entrega Completada',
        title: 'Envío entregado - {trackingNumber}',
        message: 'Tu envío {trackingNumber} ha sido entregado exitosamente',
        variables: ['trackingNumber']
      }
    ];
  }

  // Save templates to localStorage
  saveTemplates() {
    localStorage.setItem('silva_notification_templates', JSON.stringify(this.templates));
  }

  // Get current user
  async getCurrentUser() {
    return await localAuthService.getCurrentUser();
  }

  // Get notifications
  async getNotifications(filters = {}) {
    const user = await this.getCurrentUser();
    if (!user) {
      throw new Error('Usuario no autenticado');
    }

    let notifications = this.notifications.filter(n => n.userId === user.id);

    // Apply filters
    if (filters.type) {
      notifications = notifications.filter(n => n.type === filters.type);
    }

    if (filters.read !== undefined) {
      notifications = notifications.filter(n => n.read === filters.read);
    }

    if (filters.priority) {
      notifications = notifications.filter(n => n.priority === filters.priority);
    }

    if (filters.startDate) {
      notifications = notifications.filter(n => new Date(n.createdAt) >= new Date(filters.startDate));
    }

    if (filters.endDate) {
      notifications = notifications.filter(n => new Date(n.createdAt) <= new Date(filters.endDate));
    }

    // Sort by date (newest first)
    notifications.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    return notifications;
  }

  // Get unread notifications count
  async getUnreadCount() {
    const user = await this.getCurrentUser();
    if (!user) {
      return 0;
    }

    return this.notifications.filter(n => n.userId === user.id && !n.read).length;
  }

  // Mark notification as read
  async markAsRead(notificationId) {
    const user = await this.getCurrentUser();
    if (!user) {
      throw new Error('Usuario no autenticado');
    }

    const notificationIndex = this.notifications.findIndex(
      n => n.id === notificationId && n.userId === user.id
    );

    if (notificationIndex === -1) {
      throw new Error('Notificación no encontrada');
    }

    this.notifications[notificationIndex].read = true;
    this.notifications[notificationIndex].readAt = new Date().toISOString();
    this.saveNotifications();

    return {
      success: true,
      notification: this.notifications[notificationIndex],
      message: 'Notificación marcada como leída'
    };
  }

  // Mark all notifications as read
  async markAllAsRead() {
    const user = await this.getCurrentUser();
    if (!user) {
      throw new Error('Usuario no autenticado');
    }

    const userNotifications = this.notifications.filter(n => n.userId === user.id);
    const now = new Date().toISOString();

    userNotifications.forEach(notification => {
      notification.read = true;
      notification.readAt = now;
    });

    this.saveNotifications();

    return {
      success: true,
      message: 'Todas las notificaciones marcadas como leídas'
    };
  }

  // Delete notification
  async deleteNotification(notificationId) {
    const user = await this.getCurrentUser();
    if (!user) {
      throw new Error('Usuario no autenticado');
    }

    const notificationIndex = this.notifications.findIndex(
      n => n.id === notificationId && n.userId === user.id
    );

    if (notificationIndex === -1) {
      throw new Error('Notificación no encontrada');
    }

    this.notifications.splice(notificationIndex, 1);
    this.saveNotifications();

    return {
      success: true,
      message: 'Notificación eliminada exitosamente'
    };
  }

  // Create notification
  async createNotification(notificationData) {
    const user = await this.getCurrentUser();
    if (!user) {
      throw new Error('Usuario no autenticado');
    }

    const notification = {
      id: Date.now().toString(),
      userId: user.id,
      type: notificationData.type,
      title: notificationData.title,
      message: notificationData.message,
      priority: notificationData.priority || 'medium',
      read: false,
      data: notificationData.data || {},
      createdAt: new Date().toISOString()
    };

    this.notifications.push(notification);
    this.saveNotifications();

    // Send notification based on user settings
    await this.sendNotification(notification);

    return {
      success: true,
      notification,
      message: 'Notificación creada exitosamente'
    };
  }

  // Send notification
  async sendNotification(notification) {
    const user = await this.getCurrentUser();
    if (!user) return;

    const userSettings = this.settings[user.id];
    if (!userSettings) return;

    // Check quiet hours
    if (userSettings.quietHours && userSettings.quietHours.enabled) {
      const now = new Date();
      const currentHour = now.getHours();
      const startHour = parseInt(userSettings.quietHours.start.split(':')[0]);
      const endHour = parseInt(userSettings.quietHours.end.split(':')[0]);

      if (currentHour >= startHour || currentHour < endHour) {
        return; // Don't send during quiet hours
      }
    }

    // Send based on notification type and user preferences
    if (userSettings.email && userSettings.email.enabled && 
        userSettings.email.types.includes(notification.type)) {
      await this.sendEmailNotification(notification);
    }

    if (userSettings.push && userSettings.push.enabled && 
        userSettings.push.types.includes(notification.type)) {
      await this.sendPushNotification(notification);
    }

    if (userSettings.sms && userSettings.sms.enabled && 
        userSettings.sms.types.includes(notification.type)) {
      await this.sendSMSNotification(notification);
    }
  }

  // Send email notification (simulated)
  async sendEmailNotification(notification) {
    // In a real app, this would send an actual email
    console.log('Email notification sent:', notification);
  }

  // Send push notification (simulated)
  async sendPushNotification(notification) {
    // In a real app, this would send a push notification
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(notification.title, {
        body: notification.message,
        icon: '/logo192.png'
      });
    }
  }

  // Send SMS notification (simulated)
  async sendSMSNotification(notification) {
    // In a real app, this would send an actual SMS
    console.log('SMS notification sent:', notification);
  }

  // Get notification settings
  async getSettings() {
    const user = await this.getCurrentUser();
    if (!user) {
      throw new Error('Usuario no autenticado');
    }

    return this.settings[user.id] || {
      email: { enabled: true, types: [] },
      push: { enabled: true, types: [] },
      sms: { enabled: false, types: [] },
      frequency: 'immediate',
      quietHours: { enabled: false, start: '22:00', end: '08:00' }
    };
  }

  // Update notification settings
  async updateSettings(settings) {
    const user = await this.getCurrentUser();
    if (!user) {
      throw new Error('Usuario no autenticado');
    }

    this.settings[user.id] = {
      ...this.settings[user.id],
      ...settings
    };

    this.saveSettings();

    return {
      success: true,
      settings: this.settings[user.id],
      message: 'Configuración actualizada exitosamente'
    };
  }

  // Get notification templates
  async getTemplates() {
    return this.templates;
  }

  // Create notification template
  async createTemplate(templateData) {
    const user = await this.getCurrentUser();
    if (!user || user.role !== 'admin') {
      throw new Error('Acceso denegado');
    }

    const template = {
      id: templateData.id || Date.now().toString(),
      name: templateData.name,
      title: templateData.title,
      message: templateData.message,
      variables: templateData.variables || [],
      createdAt: new Date().toISOString()
    };

    this.templates.push(template);
    this.saveTemplates();

    return {
      success: true,
      template,
      message: 'Plantilla creada exitosamente'
    };
  }

  // Update notification template
  async updateTemplate(templateId, templateData) {
    const user = await this.getCurrentUser();
    if (!user || user.role !== 'admin') {
      throw new Error('Acceso denegado');
    }

    const templateIndex = this.templates.findIndex(t => t.id === templateId);
    if (templateIndex === -1) {
      throw new Error('Plantilla no encontrada');
    }

    this.templates[templateIndex] = {
      ...this.templates[templateIndex],
      ...templateData,
      updatedAt: new Date().toISOString()
    };

    this.saveTemplates();

    return {
      success: true,
      template: this.templates[templateIndex],
      message: 'Plantilla actualizada exitosamente'
    };
  }

  // Delete notification template
  async deleteTemplate(templateId) {
    const user = await this.getCurrentUser();
    if (!user || user.role !== 'admin') {
      throw new Error('Acceso denegado');
    }

    const templateIndex = this.templates.findIndex(t => t.id === templateId);
    if (templateIndex === -1) {
      throw new Error('Plantilla no encontrada');
    }

    this.templates.splice(templateIndex, 1);
    this.saveTemplates();

    return {
      success: true,
      message: 'Plantilla eliminada exitosamente'
    };
  }

  // Request notification permissions
  async requestPermissions() {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      return {
        success: permission === 'granted',
        permission,
        message: permission === 'granted' ? 
          'Permisos de notificación concedidos' : 
          'Permisos de notificación denegados'
      };
    }

    return {
      success: false,
      message: 'Las notificaciones no están soportadas en este navegador'
    };
  }

  // Get notification analytics
  async getNotificationAnalytics() {
    const user = await this.getCurrentUser();
    if (!user) {
      throw new Error('Usuario no autenticado');
    }

    const userNotifications = this.notifications.filter(n => n.userId === user.id);
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    const recentNotifications = userNotifications.filter(
      n => new Date(n.createdAt) >= thirtyDaysAgo
    );

    const totalNotifications = recentNotifications.length;
    const readNotifications = recentNotifications.filter(n => n.read).length;
    const unreadNotifications = totalNotifications - readNotifications;

    const notificationTypes = recentNotifications.reduce((acc, n) => {
      acc[n.type] = (acc[n.type] || 0) + 1;
      return acc;
    }, {});

    const priorityDistribution = recentNotifications.reduce((acc, n) => {
      acc[n.priority] = (acc[n.priority] || 0) + 1;
      return acc;
    }, {});

    return {
      totalNotifications,
      readNotifications,
      unreadNotifications,
      readRate: totalNotifications > 0 ? (readNotifications / totalNotifications) * 100 : 0,
      notificationTypes,
      priorityDistribution,
      averagePerDay: totalNotifications / 30
    };
  }

  // Bulk create notifications
  async bulkCreateNotifications(notificationsData) {
    const user = await this.getCurrentUser();
    if (!user) {
      throw new Error('Usuario no autenticado');
    }

    const createdNotifications = [];

    for (const notificationData of notificationsData) {
      const notification = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
        userId: user.id,
        type: notificationData.type,
        title: notificationData.title,
        message: notificationData.message,
        priority: notificationData.priority || 'medium',
        read: false,
        data: notificationData.data || {},
        createdAt: new Date().toISOString()
      };

      this.notifications.push(notification);
      createdNotifications.push(notification);

      // Send notification
      await this.sendNotification(notification);
    }

    this.saveNotifications();

    return {
      success: true,
      notifications: createdNotifications,
      message: `${createdNotifications.length} notificaciones creadas exitosamente`
    };
  }
}

// Create singleton instance
const localNotificationService = new LocalNotificationService();

export default localNotificationService; 